/**
 * AppLayout — authenticated MUI shell: sticky AppBar (brand, language picker,
 * operator, sign-out) and a left nav with Reporting / Configuration groups.
 *
 * Every nav item is a Coming Soon route. Built on the U1 design system
 * (`@/theme`); named Material Symbols glyphs render via the `<Icon>` wrapper.
 *
 * Sign-out: `authApi.logout()` (best-effort) → `useAuth().signOut()` → navigate
 * to `/auth/signed-out`.
 */
import {
  AppBar,
  Avatar,
  Box,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { authApi } from '@/api/auth';
import { useAuth, useCurrentOperator, type CurrentOperator } from '@/auth/AuthContext';
import { useCopy } from '@/hooks/useCopy';
import { COURT_GREEN, Icon, initialsOf } from '@/theme';
import { LanguageSwitcher } from './LanguageSwitcher';
import { NAV_GROUPS, type NavItem } from './nav';

const NAV_WIDTH = 248;
const APP_BAR_HEIGHT = 64;

/**
 * Logo glyph — single swappable token (neutral; pending the re-theme spec).
 */
const LOGO_GLYPH = 'dashboard';

/** Human-readable role label for the user chip. Same precedence as WSRADM-0010. */
function roleLabel(
  operator: CurrentOperator,
  t: (key: string, fallback?: string) => string,
): string {
  if (operator.isPlatformSupport) return t('role.platformSupport', 'Platform support');
  if (operator.roles.includes('OWNER')) return t('role.owner', 'Owner');
  if (operator.roles.includes('SUPER_ADMIN')) return t('role.superAdmin', 'Super Admin');
  if (operator.roles.includes('ADMIN')) return t('role.admin', 'Admin');
  return operator.roles[0] ?? t('role.member', 'Member');
}

export function AppLayout() {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const operator = useCurrentOperator();
  const { t } = useCopy();
  const [signingOut, setSigningOut] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState<HTMLElement | null>(null);
  const brand = t('app.brand', 'WSReporting');

  const handleSignOut = useCallback(async () => {
    setSigningOut(true);
    try {
      await authApi.logout();
    } catch {
      // A failed network logout must not block clearing the local session.
    }
    signOut();
    navigate('/auth/signed-out', { replace: true });
  }, [navigate, signOut]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Box
        component="nav"
        aria-label="Primary"
        sx={{
          width: NAV_WIDTH,
          flexShrink: 0,
          position: 'sticky',
          top: 0,
          alignSelf: 'flex-start',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          borderRight: '1px solid rgba(0,0,0,0.12)',
        }}
      >
        <Box
          sx={{
            height: APP_BAR_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: 2.25,
            borderBottom: '1px solid rgba(0,0,0,0.08)',
          }}
        >
          <Avatar
            variant="rounded"
            aria-label={brand}
            sx={{ bgcolor: COURT_GREEN, width: 34, height: 34 }}
          >
            <Icon name={LOGO_GLYPH} sx={{ fontSize: 21, color: '#fff' }} />
          </Avatar>
          <Typography sx={{ fontSize: 19, fontWeight: 700, letterSpacing: '-0.5px' }}>
            {brand}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, overflowY: 'auto', py: 0.5 }}>
          {NAV_GROUPS.map((group) => (
            <Box key={group.groupKey} sx={{ mt: 0.5 }}>
              {/* Section heading lives outside <List> so the <ul> only contains <li>. */}
              <Box sx={{ px: 2, pt: 1, pb: 0.25 }}>
                <Typography
                  variant="caption"
                  component="p"
                  sx={{ letterSpacing: '0.08em', fontWeight: 600, color: 'text.secondary' }}
                >
                  {t(group.groupKey, group.groupFallback)}
                </Typography>
              </Box>
              <List dense>
                {group.items.map((item) => (
                  <NavLinkItem key={item.to} item={item} t={t} />
                ))}
              </List>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <AppBar position="sticky" sx={{ height: APP_BAR_HEIGHT }}>
          <Toolbar sx={{ gap: 2, minHeight: APP_BAR_HEIGHT, height: APP_BAR_HEIGHT }}>
            <Box sx={{ flex: 1 }} />
            <LanguageSwitcher />
            {operator?.isPlatformSupport ? (
              <Chip
                icon={<Icon name="support_agent" sx={{ fontSize: 18 }} />}
                label={t('shell.platformSupportSession', 'Platform support session')}
                size="small"
                sx={{
                  bgcolor: '#FFF7E8',
                  color: '#7A5510',
                  border: '1px solid #F2DDB0',
                  fontWeight: 500,
                  '& .MuiChip-icon': { color: 'inherit' },
                }}
              />
            ) : null}
            {operator ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Avatar sx={{ bgcolor: COURT_GREEN, width: 34, height: 34, fontSize: 13 }}>
                  {initialsOf(operator.name ?? operator.email)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: 13, fontWeight: 500 }}>
                    {operator.name ?? operator.email}
                  </Typography>
                  <Typography noWrap sx={{ fontSize: 11, color: 'rgba(0,0,0,0.55)' }}>
                    {roleLabel(operator, t)}
                  </Typography>
                </Box>
                <Tooltip title={t('nav.account', 'Account')}>
                  <IconButton
                    size="small"
                    aria-label={t('nav.accountMenu', 'Account menu')}
                    aria-haspopup="menu"
                    onClick={(e) => setUserMenuAnchor(e.currentTarget)}
                    disabled={signingOut}
                  >
                    <Icon name="more_vert" sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={userMenuAnchor}
                  open={Boolean(userMenuAnchor)}
                  onClose={() => setUserMenuAnchor(null)}
                >
                  <MenuItem
                    onClick={() => {
                      setUserMenuAnchor(null);
                      void handleSignOut();
                    }}
                    disabled={signingOut}
                  >
                    <ListItemIcon>
                      <Icon name="logout" sx={{ fontSize: 20 }} />
                    </ListItemIcon>
                    {t('nav.signOut', 'Sign out')}
                  </MenuItem>
                </Menu>
              </Box>
            ) : null}
          </Toolbar>
        </AppBar>

        <Box component="main" sx={{ flex: 1, overflowY: 'auto', px: 4, pt: 3.5, pb: 10 }}>
          <MainContent />
        </Box>
      </Box>
    </Box>
  );
}

function MainContent() {
  return (
    <Box data-testid="app-main-content" sx={{ maxWidth: '90%', mx: 'auto' }}>
      <Outlet />
    </Box>
  );
}

interface NavLinkItemProps {
  item: NavItem;
  t: (key: string, fallback?: string) => string;
}

function NavLinkItem({ item, t }: NavLinkItemProps) {
  return (
    <ListItem disablePadding>
      <ListItemButton
        component={NavLink}
        to={item.to}
        end={item.end === true}
        sx={{
          '&.active': {
            bgcolor: 'action.selected',
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 36 }}>
          <Icon name={item.icon} />
        </ListItemIcon>
        <ListItemText primary={t(item.key, item.fallback)} />
      </ListItemButton>
    </ListItem>
  );
}

export default AppLayout;
